// ===================================================================
// VolunteerOS -- Volunteer Roster
// Search, filter, CRUD, CSV import/export, detail modal with tabs
// ===================================================================

import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  ActionIcon, Modal, Stack, Tabs, FileButton, Pagination,
  Checkbox, SimpleGrid, ScrollArea,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconDownload,
  IconUpload, IconUser, IconPhone, IconCertificate, IconNotes,
  IconRefresh, IconCalendar, IconShieldCheck, IconId,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import Papa from 'papaparse';
import { formatPhone } from '../../lib/phoneFormat';
import {
  useVolunteerStore,
  type Volunteer,
  parseExtras,
} from '../../store/volunteerStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { useNavigate } from 'react-router-dom';
import { queryRecords } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';

// ── Directory volunteer panel ─────────────────────────────────────

type DirVol = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  skills: string;
  status: string;
};

function DirectoryVolunteersPanel({ rosterEmails }: { rosterEmails: Set<string> }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dirVols, setDirVols] = useState<DirVol[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState<number | null>(null);
  const [dirSearch, setDirSearch] = useState('');

  useEffect(() => {
    if (!TABLES.people) return;
    const FP = FIELDS.people;
    queryRecords(TABLES.people, {
      filters: [
        { field: FP.type, operator: 'eq', value: 'volunteer' },
        { field: FP.status, operator: 'eq', value: 'active' },
      ],
      limit: 1,
    }).then(r => setTotal(r.total)).catch(() => {});
  }, []);

  const loadDirVols = async () => {
    if (!TABLES.people || loading) return;
    setLoading(true);
    try {
      const FP = FIELDS.people;
      const r = await queryRecords<Record<string, unknown>>(TABLES.people, {
        filters: [
          { field: FP.type, operator: 'eq', value: 'volunteer' },
          { field: FP.status, operator: 'eq', value: 'active' },
        ],
        sort: { field: FP.lastName, direction: 'asc' },
        limit: 200,
      });
      setDirVols(r.records.map(p => ({
        id: p.record_id as number,
        firstName: String(p[FP.firstName] ?? ''),
        lastName: String(p[FP.lastName] ?? ''),
        email: String(p[FP.email] ?? ''),
        phone: String(p[FP.phone] ?? ''),
        skills: String(p[FP.skills] ?? ''),
        status: String(p[FP.status] ?? 'active'),
      })));
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  const toggleOpen = () => {
    if (!open && dirVols.length === 0) loadDirVols();
    setOpen(o => !o);
  };

  if (!TABLES.people || total === null) return null;

  const filtered = dirSearch
    ? dirVols.filter(v => `${v.firstName} ${v.lastName} ${v.email}`.toLowerCase().includes(dirSearch.toLowerCase()))
    : dirVols;

  return (
    <div style={{ marginBottom: 16, border: '1px solid #bbf7d0', borderRadius: 4, overflow: 'hidden' }}>
      <div
        onClick={toggleOpen}
        style={{ padding: '8px 16px', background: '#f0fdf4', display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
        <span style={{ fontWeight: 600, color: '#166534' }}>People Directory</span>
        <span style={{ color: '#374151' }}>{total} active volunteer{total !== 1 ? 's' : ''} on file</span>
        <span style={{ color: '#6b7280', fontSize: 12 }}>
          — People added in <strong>Administration → People</strong> (type: Volunteer) appear here automatically.
        </span>
        <button
          onClick={e => { e.stopPropagation(); navigate('/admin/people?type=volunteer'); }}
          style={{ marginLeft: 'auto', fontSize: 12, color: '#16a34a', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
          Manage in Directory →
        </button>
        <span style={{ color: '#166534', fontSize: 12, fontWeight: 600 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ padding: 16, background: 'white' }}>
          <input
            type="text"
            placeholder="Search directory volunteers..."
            value={dirSearch}
            onChange={e => setDirSearch(e.target.value)}
            style={{ width: '100%', padding: '8px 8px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 14, marginBottom: 8, boxSizing: 'border-box' }}
          />
          {loading ? (
            <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 14, padding: 16 }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 14, padding: 16 }}>
              {dirVols.length === 0 ? 'No directory volunteers found.' : 'No results match your search.'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 600, color: '#374151' }}>Name</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 600, color: '#374151' }}>Email</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 600, color: '#374151' }}>Skills</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 600, color: '#374151' }}>Roster</th>
                    <th style={{ padding: '8px 8px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(dv => {
                    const inRoster = dv.email ? rosterEmails.has(dv.email.toLowerCase()) : false;
                    return (
                      <tr key={dv.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px 8px', fontWeight: 600 }}>{dv.firstName} {dv.lastName}</td>
                        <td style={{ padding: '8px 8px', fontSize: 12, color: '#6b7280' }}>{dv.email || '—'}</td>
                        <td style={{ padding: '8px 8px', fontSize: 12 }}>{dv.skills ? dv.skills.slice(0, 60) : '—'}</td>
                        <td style={{ padding: '8px 8px' }}>
                          {inRoster
                            ? <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>✓ On Roster</span>
                            : <span style={{ fontSize: 12, color: '#9ca3af' }}>Not enrolled</span>}
                        </td>
                        <td style={{ padding: '8px 8px' }}>
                          <button
                            onClick={() => navigate(`/admin/people/${dv.id}`)}
                            style={{ fontSize: 12, color: '#4b5563', background: 'none', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}>
                            View Profile
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── constants ────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'probationary', label: 'Probationary' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'dnd', label: 'Do Not Deploy' },
];

const ROLE_OPTIONS = [
  { value: 'all', label: 'All Roles' },
  { value: 'active', label: 'Active / Field' },
  { value: 'office', label: 'Office' },
  { value: 'medical', label: 'Medical' },
  { value: 'comms', label: 'Communications' },
  { value: 'both', label: 'Both' },
];

const VEHICLE_OPTIONS = [
  { value: 'all', label: 'Any Vehicle' },
  { value: 'yes', label: 'Has Vehicle' },
  { value: 'no', label: 'No Vehicle' },
];

const ROLE_PREF_SELECT = [
  { value: 'active', label: 'Field' },
  { value: 'office', label: 'Admin' },
  { value: 'medical', label: 'Medical' },
  { value: 'comms', label: 'Comms' },
  { value: 'both', label: 'Flexible' },
];

const SHIRT_SIZES = [
  { value: 'XS', label: 'XS' },
  { value: 'S', label: 'S' },
  { value: 'M', label: 'M' },
  { value: 'L', label: 'L' },
  { value: 'XL', label: 'XL' },
  { value: '2XL', label: '2XL' },
  { value: '3XL', label: '3XL' },
];

const VEHICLE_SELECT = [
  { value: 'none', label: 'None' },
  { value: 'personal', label: 'Personal' },
  { value: 'transport', label: 'Can Transport Others' },
];

const CERT_OPTIONS = [
  'CPR/AED', 'First Aid', 'CERT', 'ICS 100', 'ICS 200', 'ICS 300',
  'ICS 700', 'Ham Radio', 'Chainsaw', 'Swift Water', 'Wilderness FA',
];

const SKILL_OPTIONS = [
  'Medical', 'Communications', 'Logistics', 'Search & Rescue',
  'Heavy Equipment', 'Counseling', 'Translation', 'Cooking',
  'Construction', 'IT/Tech',
];

const EQUIPMENT_OPTIONS = [
  'Chainsaw', 'Generator', 'Water Pump', 'ATV', 'Boat',
  'Trailer', 'Ham Radio', 'Medical Bag',
];

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const DAY_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};
const PERIODS = ['morning', 'afternoon', 'evening', 'overnight'] as const;
const PERIOD_LABELS: Record<string, string> = {
  morning: 'AM', afternoon: 'PM', evening: 'Eve', overnight: 'Night',
};

const WAIVER_TEXT = `VOLUNTEER LIABILITY WAIVER AND RELEASE OF LIABILITY

I, the undersigned volunteer, hereby acknowledge and agree to the following:

1. ASSUMPTION OF RISK: I understand that volunteering for emergency management activities involves inherent risks, including but not limited to: physical injury, exposure to hazardous materials or conditions, emotional distress, property damage, and other dangers associated with disaster response and recovery operations.

2. RELEASE OF LIABILITY: I hereby release, waive, discharge, and covenant not to sue the organization, its officers, directors, employees, agents, and representatives from any and all liability, claims, demands, actions, or causes of action arising out of or related to any loss, damage, or injury that may be sustained by me during my volunteer activities.

3. INDEMNIFICATION: I agree to indemnify and hold harmless the organization from any loss, liability, damage, or cost it may incur due to my participation in volunteer activities.

4. MEDICAL AUTHORIZATION: In the event of an emergency, I authorize the organization to secure emergency medical treatment on my behalf. I understand that the organization does not provide medical insurance for volunteers.

5. PHOTO/MEDIA RELEASE: I grant permission to the organization to use my image, likeness, and voice in photographs, video, and other media for promotional and educational purposes.

6. CODE OF CONDUCT: I agree to follow all safety protocols, instructions from supervisors, and the organization's code of conduct while serving as a volunteer.

7. CONFIDENTIALITY: I agree to maintain the confidentiality of any sensitive information I may encounter during my volunteer service.

This waiver is binding upon me, my heirs, executors, administrators, and assigns. I have read this document carefully and understand its contents. I sign it voluntarily and with full knowledge of its significance.`;

const PAGE_SIZE = 20;

function statusColor(s: string): string {
  switch (s) {
    case 'active': return 'green';
    case 'probationary': return 'yellow';
    case 'inactive': return 'gray';
    case 'dnd': return 'red';
    default: return 'gray';
  }
}

// Helper: toggle item in comma-separated string
function toggleCsv(csv: string | undefined | null, item: string): string {
  const arr = String(csv || '').split(',').map((s) => s.trim()).filter(Boolean);
  const idx = arr.indexOf(item);
  if (idx >= 0) arr.splice(idx, 1);
  else arr.push(item);
  return arr.join(', ');
}

function csvIncludes(csv: string | undefined | null, item: string): boolean {
  return String(csv || '').split(',').map((s) => s.trim()).includes(item);
}

// ── component ────────────────────────────────────────────────────

export default function Roster() {
  const { canEdit } = useModulePermission('volunteer');
  const {
    volunteers, loadVolunteers,
    createVolunteer, updateVolunteer, deleteVolunteer,
  } = useVolunteerStore();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  // modals
  const [detailVol, setDetailVol] = useState<Volunteer | null>(null);
  const [editVol, setEditVol] = useState<Volunteer | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Volunteer | null>(null);
  const [saving, setSaving] = useState(false);
  const [formTab, setFormTab] = useState<string | null>('contact');

  // form fields — Tab 1: Contact
  const [formFirstName, setFormFirstName] = useState('');
  const [formLastName, setFormLastName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formOrg, setFormOrg] = useState('');
  const [formStatus, setFormStatus] = useState<string | null>('active');
  const [formDob, setFormDob] = useState('');
  const [formAddress, setFormAddress] = useState('');
  const [formCity, setFormCity] = useState('');
  const [formState, setFormState] = useState('');
  const [formZip, setFormZip] = useState('');
  const [formShirtSize, setFormShirtSize] = useState<string | null>(null);
  const [formDietary, setFormDietary] = useState('');

  // form fields — Tab 2: Emergency Contact
  const [formEcName, setFormEcName] = useState('');
  const [formEcRelationship, setFormEcRelationship] = useState('');
  const [formEcPhone, setFormEcPhone] = useState('');
  const [formEcEmail, setFormEcEmail] = useState('');

  // form fields — Tab 3: Skills & Availability
  const [formCerts, setFormCerts] = useState('');
  const [formSkills, setFormSkills] = useState('');
  const [formEquipment, setFormEquipment] = useState('');
  const [formLanguages, setFormLanguages] = useState('');
  const [formRolePref, setFormRolePref] = useState<string | null>('active');
  const [formVehicle, setFormVehicle] = useState<string | null>(null);

  // form fields — Tab 4: Availability grid
  const [formAvailability, setFormAvailability] = useState<Record<string, Record<string, boolean>>>({});

  // form fields — Tab 5: Waiver
  const [formWaiverAccepted, setFormWaiverAccepted] = useState(false);
  const [formWaiverDate, setFormWaiverDate] = useState('');

  const [_formNotes, setFormNotes] = useState('');

  const initialLoad = useRef(false);

  useEffect(() => {
    if (initialLoad.current) return;
    initialLoad.current = true;
    loadVolunteers();
  }, [loadVolunteers]);

  // ── filtering / sorting ────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = volunteers;
    if (statusFilter !== 'all') list = list.filter((v) => v.status === statusFilter);
    if (roleFilter !== 'all') list = list.filter((v) => (parseExtras(v.notes).rolePref || 'active') === roleFilter);
    if (vehicleFilter !== 'all') {
      list = list.filter((v) => {
        const extras = parseExtras(v.notes);
        const hasVehicle = !!extras.vehicle;
        return vehicleFilter === 'yes' ? hasVehicle : !hasVehicle;
      });
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((v) =>
        `${v.firstName || ''} ${v.lastName || ''}`.toLowerCase().includes(q) ||
        (v.email || '').toLowerCase().includes(q) ||
        (v.phone || '').includes(q) ||
        (parseExtras(v.notes).org as string || '').toLowerCase().includes(q) ||
        (v.certifications || '').toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) =>
      `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`),
    );
  }, [volunteers, statusFilter, roleFilter, vehicleFilter, search]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [statusFilter, roleFilter, vehicleFilter, search]);

  const sorted = [...filtered].sort((a, b) => {
    if (!sortField) return 0;
    const va = (a as any)[sortField] ?? '';
    const vb = (b as any)[sortField] ?? '';
    const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    return sortDir === 'asc' ? n : -n;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paged = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Emails of roster volunteers — used to mark directory volunteers as enrolled
  const rosterEmails = useMemo(() => {
    const s = new Set<string>();
    volunteers.forEach(v => { if (v.email) s.add(v.email.toLowerCase()); });
    return s;
  }, [volunteers]);

  // ── form helpers ───────────────────────────────────────────────
  const resetForm = useCallback(() => {
    setFormFirstName('');
    setFormLastName('');
    setFormEmail('');
    setFormPhone('');
    setFormOrg('');
    setFormStatus('active');
    setFormDob('');
    setFormAddress('');
    setFormCity('');
    setFormState('');
    setFormZip('');
    setFormShirtSize(null);
    setFormDietary('');
    setFormEcName('');
    setFormEcRelationship('');
    setFormEcPhone('');
    setFormEcEmail('');
    setFormCerts('');
    setFormSkills('');
    setFormEquipment('');
    setFormLanguages('');
    setFormRolePref('active');
    setFormVehicle(null);
    setFormAvailability({});
    setFormWaiverAccepted(false);
    setFormWaiverDate('');
    setFormNotes('');
    setFormTab('contact');
  }, []);

  const populateForm = useCallback((v: Volunteer) => {
    const extras = parseExtras(v.notes);
    setFormFirstName(v.firstName);
    setFormLastName(v.lastName);
    setFormEmail(v.email);
    setFormPhone(v.phone);
    setFormOrg((extras.org as string) || '');
    setFormStatus(v.status || 'active');
    setFormDob((extras.dob as string) || '');
    setFormAddress((extras.address as string) || '');
    setFormCity((extras.city as string) || '');
    setFormState((extras.state as string) || '');
    setFormZip((extras.zip as string) || '');
    setFormShirtSize((extras.shirtSize as string) || null);
    setFormDietary((extras.dietary as string) || '');
    setFormEcName(v.emergencyContact || '');
    setFormEcRelationship((extras.ecRelationship as string) || '');
    setFormEcPhone(v.emergencyPhone || '');
    setFormEcEmail((extras.ecEmail as string) || '');
    setFormCerts(v.certifications || '');
    setFormSkills(v.skills || '');
    setFormEquipment((extras.equipment as string) || '');
    setFormLanguages((extras.languages as string) || '');
    setFormRolePref((extras.rolePref as string) || 'active');
    setFormVehicle((extras.vehicle as string) || null);
    setFormWaiverAccepted(!!(extras.waiverAccepted));
    setFormWaiverDate((extras.waiverDate as string) || '');
    setFormNotes(v.notes);
    setFormTab('contact');
    // parse availability
    try {
      const avail = JSON.parse(v.availability || '{}');
      setFormAvailability(typeof avail === 'object' && avail !== null ? avail : {});
    } catch {
      setFormAvailability({});
    }
  }, []);

  const openCreate = () => { resetForm(); setShowCreate(true); };
  const openEdit = (v: Volunteer) => { populateForm(v); setEditVol(v); };

  const toggleAvailability = (day: string, period: string) => {
    setFormAvailability((prev) => {
      const daySlots = { ...(prev[day] || {}) };
      daySlots[period] = !daySlots[period];
      return { ...prev, [day]: daySlots };
    });
  };

  const handleSave = async () => {
    if (!formFirstName.trim() || !formLastName.trim()) {
      notifications.show({ message: 'First and last name are required', color: '#C68700' });
      return;
    }
    setSaving(true);
    try {
      // Build extras object for notes JSON
      const extras: Record<string, unknown> = {};
      if (formDob) extras.dob = formDob;
      if (formAddress) extras.address = formAddress;
      if (formCity) extras.city = formCity;
      if (formState) extras.state = formState;
      if (formZip) extras.zip = formZip;
      if (formShirtSize) extras.shirtSize = formShirtSize;
      if (formDietary) extras.dietary = formDietary;
      if (formEcRelationship) extras.ecRelationship = formEcRelationship;
      if (formEcEmail) extras.ecEmail = formEcEmail;
      if (formVehicle) extras.vehicle = formVehicle;
      if (formEquipment) extras.equipment = formEquipment;
      if (formLanguages) extras.languages = formLanguages;
      // org and rolePref stored in extras (no dedicated Ignatius column)
      if (formOrg) extras.org = formOrg.trim();
      if (formRolePref) extras.rolePref = formRolePref;
      if (formWaiverAccepted) {
        extras.waiverAccepted = true;
        extras.waiverDate = formWaiverDate || new Date().toISOString();
      }

      // Preserve any extra keys that were in the original notes but not managed by this form
      const existingExtras = editVol ? parseExtras(editVol.notes) : {};
      const managedKeys = [
        'dob', 'address', 'city', 'state', 'zip', 'shirtSize', 'dietary',
        'ecRelationship', 'ecEmail', 'vehicle', 'equipment', 'languages',
        'waiverAccepted', 'waiverDate', 'org', 'rolePref',
      ];
      for (const [k, v] of Object.entries(existingExtras)) {
        if (!managedKeys.includes(k) && !(k in extras)) {
          extras[k] = v;
        }
      }

      const notesJson = Object.keys(extras).length > 0 ? JSON.stringify(extras) : '';

      const payload = {
        firstName: formFirstName.trim(),
        lastName: formLastName.trim(),
        email: formEmail.trim(),
        phone: formPhone.trim(),
        status: formStatus || 'active',
        certifications: formCerts.trim(),
        skills: formSkills.trim(),
        availability: JSON.stringify(formAvailability),
        emergencyContact: formEcName.trim(),
        emergencyPhone: formEcPhone.trim(),
        notes: notesJson,
        registeredAt: editVol?.registeredAt || new Date().toISOString(),
      };
      if (editVol) {
        await updateVolunteer(editVol.id, payload);
        notifications.show({ message: 'Volunteer updated', color: 'var(--color-success)' });
        setEditVol(null);
      } else {
        await createVolunteer(payload);
        notifications.show({ message: 'Volunteer created', color: 'var(--color-success)' });
        setShowCreate(false);
      }
      resetForm();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      notifications.show({ message: msg, color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteVolunteer(deleteTarget.id);
      notifications.show({
        message: `${deleteTarget.firstName} ${deleteTarget.lastName} removed`,
        color: '#939598',
      });
      setDeleteTarget(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      notifications.show({ message: msg, color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  // ── CSV import ─────────────────────────────────────────────────
  const handleImport = (file: File | null) => {
    if (!file) return;
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        let imported = 0;
        const errors: string[] = [];
        for (let i = 0; i < results.data.length; i++) {
          const row = results.data[i];
          const firstName = (row.firstName || row.first_name || '').trim();
          const lastName = (row.lastName || row.last_name || '').trim();
          if (!firstName && !lastName) { errors.push(`Row ${i + 2}: missing name`); continue; }
          try {
            await createVolunteer({
              firstName,
              lastName,
              email: (row.email || '').trim(),
              phone: (row.phone || '').trim(),
              org: (row.org || row.organization || '').trim(),
              rolePref: (row.role || row.rolePref || 'active').trim(),
              status: (row.status || 'active').trim(),
              certs: (row.certs || '').trim(),
              skills: (row.skills || '').trim(),
              availability: '{}',
              ecName: '',
              ecPhone: '',
              notes: '',
              registeredAt: new Date().toISOString(),
            });
            imported++;
          } catch (err) {
            errors.push(`Row ${i + 2} (${firstName} ${lastName}): ${err instanceof Error ? err.message : 'failed'}`);
          }
        }
        if (errors.length > 0) {
          notifications.show({
            title: `Imported ${imported}, ${errors.length} failed`,
            message: errors.slice(0, 5).join('\n') + (errors.length > 5 ? `\n...and ${errors.length - 5} more` : ''),
            color: '#F86A0B',
            autoClose: false,
          });
        } else {
          notifications.show({ message: `Imported ${imported} volunteer(s)`, color: 'var(--color-success)' });
        }
      },
    });
  };

  // ── CSV export ─────────────────────────────────────────────────
  const handleExport = () => {
    const rows = filtered.map((v) => ({
      firstName: v.firstName,
      lastName: v.lastName,
      email: v.email,
      phone: v.phone,
      org: parseExtras(v.notes).org || '',
      rolePref: parseExtras(v.notes).rolePref || 'active',
      status: v.status,
      certs: v.certifications,
      skills: v.skills,
    }));
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `volunteers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── quick status change ────────────────────────────────────────
  const handleQuickStatus = async (v: Volunteer, newStatus: string) => {
    try {
      await updateVolunteer(v.id, { status: newStatus });
      notifications.show({
        message: `${v.firstName} ${v.lastName} set to ${newStatus}`,
        color: statusColor(newStatus),
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Update failed';
      notifications.show({ message: msg, color: 'var(--color-error)' });
    }
  };

  // ── tabbed form modal body (shared by create & edit) ───────────
  const formBody = (
    <Stack gap={0}>
      <Tabs value={formTab} onChange={setFormTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="contact">Contact</Tabs.Tab>
          <Tabs.Tab value="emergency">Emergency</Tabs.Tab>
          <Tabs.Tab value="skills">Skills</Tabs.Tab>
          <Tabs.Tab value="availability">Availability</Tabs.Tab>
          <Tabs.Tab value="waiver">Waiver</Tabs.Tab>
        </Tabs.List>

        {/* Tab 1: Contact */}
        <Tabs.Panel value="contact">
          <Stack gap="sm">
            <Group grow>
              <TextInput label="First Name" required value={formFirstName}
                onChange={(e) => setFormFirstName(e.target.value)} size="sm" />
              <TextInput label="Last Name" required value={formLastName}
                onChange={(e) => setFormLastName(e.target.value)} size="sm" />
            </Group>
            <Group grow>
              <TextInput label="Email" value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)} size="sm" />
              <TextInput label="Phone" value={formPhone}
                onChange={(e) => setFormPhone(e.target.value)} size="sm" />
            </Group>
            <TextInput label="Organization" value={formOrg}
              onChange={(e) => setFormOrg(e.target.value)} size="sm" />
            <Group grow>
              <TextInput label="Date of Birth" type="date" value={formDob}
                onChange={(e) => setFormDob(e.target.value)} size="sm" />
              <Select label="Status" data={STATUS_OPTIONS.filter((s) => s.value !== 'all')}
                value={formStatus} onChange={setFormStatus} size="sm" />
            </Group>
            <TextInput label="Address" value={formAddress}
              onChange={(e) => setFormAddress(e.target.value)} size="sm" />
            <Group grow>
              <TextInput label="City" value={formCity}
                onChange={(e) => setFormCity(e.target.value)} size="sm" />
              <TextInput label="State" value={formState}
                onChange={(e) => setFormState(e.target.value)} size="sm" style={{ maxWidth: 100 }} />
              <TextInput label="ZIP" value={formZip}
                onChange={(e) => setFormZip(e.target.value)} size="sm" style={{ maxWidth: 120 }} />
            </Group>
            <Group grow>
              <Select label="T-Shirt Size" data={SHIRT_SIZES} placeholder="-- Select --"
                value={formShirtSize} onChange={setFormShirtSize} size="sm" clearable />
              <TextInput label="Dietary Restrictions / Allergies" value={formDietary}
                onChange={(e) => setFormDietary(e.target.value)} size="sm" />
            </Group>
          </Stack>
        </Tabs.Panel>

        {/* Tab 2: Emergency Contact */}
        <Tabs.Panel value="emergency">
          <Stack gap="sm">
            <TextInput label="Emergency Contact Name" value={formEcName}
              onChange={(e) => setFormEcName(e.target.value)} size="sm" />
            <TextInput label="Relationship" value={formEcRelationship}
              onChange={(e) => setFormEcRelationship(e.target.value)} size="sm" />
            <Group grow>
              <TextInput label="Emergency Phone" value={formEcPhone}
                onChange={(e) => setFormEcPhone(e.target.value)} size="sm" />
              <TextInput label="Emergency Email" value={formEcEmail}
                onChange={(e) => setFormEcEmail(e.target.value)} size="sm" />
            </Group>
          </Stack>
        </Tabs.Panel>

        {/* Tab 3: Skills */}
        <Tabs.Panel value="skills">
          <Stack gap="md">
            <div>
              <Text size="sm" fw={600} mb={6}>Certifications</Text>
              <Group gap="xs" wrap="wrap">
                {CERT_OPTIONS.map((cert) => (
                  <Checkbox key={cert} label={cert} size="xs"
                    checked={csvIncludes(formCerts, cert)}
                    onChange={() => setFormCerts(toggleCsv(formCerts, cert))} />
                ))}
              </Group>
            </div>
            <div>
              <Text size="sm" fw={600} mb={6}>Skills</Text>
              <Group gap="xs" wrap="wrap">
                {SKILL_OPTIONS.map((skill) => (
                  <Checkbox key={skill} label={skill} size="xs"
                    checked={csvIncludes(formSkills, skill)}
                    onChange={() => setFormSkills(toggleCsv(formSkills, skill))} />
                ))}
              </Group>
            </div>
            <div>
              <Text size="sm" fw={600} mb={6}>Equipment Available</Text>
              <Group gap="xs" wrap="wrap">
                {EQUIPMENT_OPTIONS.map((eq) => (
                  <Checkbox key={eq} label={eq} size="xs"
                    checked={csvIncludes(formEquipment, eq)}
                    onChange={() => setFormEquipment(toggleCsv(formEquipment, eq))} />
                ))}
              </Group>
            </div>
            <TextInput label="Languages Spoken (comma-separated)" value={formLanguages}
              onChange={(e) => setFormLanguages(e.target.value)} size="sm" />
            <Group grow>
              <Select label="Role Preference" data={ROLE_PREF_SELECT}
                value={formRolePref} onChange={setFormRolePref} size="sm" />
              <Select label="Vehicle" data={VEHICLE_SELECT} placeholder="None"
                value={formVehicle} onChange={setFormVehicle} size="sm" clearable />
            </Group>
          </Stack>
        </Tabs.Panel>

        {/* Tab 4: Availability Grid */}
        <Tabs.Panel value="availability">
          <Stack gap="sm">
            <Text size="sm" c="dimmed">Click cells to toggle availability</Text>
            <SimpleGrid cols={8} spacing={4}>
              {/* Header row */}
              <Paper p={4} radius="sm" />
              {DAYS.map((day) => (
                <Paper key={day} p={4} radius="sm" bg="#f1f5f9" ta="center">
                  <Text size="xs" fw={700}>{DAY_LABELS[day]}</Text>
                </Paper>
              ))}
              {/* Period rows */}
              {PERIODS.map((period) => (
                <>
                  <Paper key={`label-${period}`} p={4} radius="sm" bg="#f1f5f9">
                    <Text size="xs" fw={600} ta="center">{PERIOD_LABELS[period]}</Text>
                  </Paper>
                  {DAYS.map((day) => {
                    const active = !!(formAvailability[day]?.[period]);
                    return (
                      <Paper
                        key={`${day}-${period}`}
                        p={4}
                        radius="sm"
                        withBorder
                        ta="center"
                        style={{
                          cursor: 'pointer',
                          backgroundColor: active ? 'rgba(3,119,48,0.1)' : undefined,
                          borderColor: active ? 'var(--color-success)' : undefined,
                        }}
                        onClick={() => toggleAvailability(day, period)}
                      >
                        <Text size="xs" fw={active ? 700 : 400} c={active ? 'var(--color-success)' : 'dimmed'}>
                          {active ? '\u2713' : '\u2014'}
                        </Text>
                      </Paper>
                    );
                  })}
                </>
              ))}
            </SimpleGrid>
          </Stack>
        </Tabs.Panel>

        {/* Tab 5: Waiver */}
        <Tabs.Panel value="waiver">
          <Stack gap="sm">
            <ScrollArea h={250} type="auto" offsetScrollbars>
              <Paper p="md" radius="sm" withBorder>
                <Text size="xs" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {WAIVER_TEXT}
                </Text>
              </Paper>
            </ScrollArea>
            <Checkbox
              label="I have read and agree to the Volunteer Liability Waiver & Release"
              checked={formWaiverAccepted}
              onChange={(e) => {
                setFormWaiverAccepted(e.currentTarget.checked);
                if (e.currentTarget.checked && !formWaiverDate) {
                  setFormWaiverDate(new Date().toISOString());
                }
              }}
              size="sm"
            />
            {formWaiverDate && (
              <Text size="xs" c="dimmed">
                Waiver accepted: {new Date(formWaiverDate).toLocaleDateString()}
              </Text>
            )}
          </Stack>
        </Tabs.Panel>
      </Tabs>

      <Group justify="flex-end" mt="md">
        <Button variant="light" onClick={() => { setShowCreate(false); setEditVol(null); resetForm(); }}>
          Cancel
        </Button>
        <Button onClick={handleSave} loading={saving}>
          {editVol ? 'Update' : 'Create'}
        </Button>
      </Group>
    </Stack>
  );

  // ── credentials (directory) ────────────────────────────────────
  const [detailCreds, setDetailCreds] = useState<{ record_id: number; name: string; type: string; issuingBody: string; certCode: string; issuedDate: string; expiresDate: string; status: string }[]>([]);
  const [detailCredsLoading, setDetailCredsLoading] = useState(false);

  useEffect(() => {
    if (!detailVol || !TABLES.credentials || !FIELDS.credentials.personId) return;
    const personIdField = FIELDS.volunteers.personId;
    if (!personIdField) return;
    const linkedPersonId = (detailVol as any)[personIdField];
    if (!linkedPersonId) return;
    setDetailCredsLoading(true);
    queryRecords(TABLES.credentials, {
      filters: [{ field: FIELDS.credentials.personId, operator: 'eq', value: String(linkedPersonId) }],
      limit: 50,
    }).then(res => {
      setDetailCreds(res.records.map(r => ({
        record_id: r.record_id as number,
        name: String(r[FIELDS.credentials.name] ?? ''),
        type: String(r[FIELDS.credentials.type] ?? ''),
        issuingBody: String(r[FIELDS.credentials.issuingBody] ?? ''),
        certCode: String(r[FIELDS.credentials.certCode] ?? ''),
        issuedDate: String(r[FIELDS.credentials.issuedDate] ?? ''),
        expiresDate: String(r[FIELDS.credentials.expiresDate] ?? ''),
        status: String(r[FIELDS.credentials.status] ?? ''),
      })));
    }).catch(() => {}).finally(() => setDetailCredsLoading(false));
  }, [detailVol]);

  // ── detail modal ───────────────────────────────────────────────
  const detailExtras = detailVol ? parseExtras(detailVol.notes) : {};

  // ── render ─────────────────────────────────────────────────────
  return (
    <div>
      <DirectoryVolunteersPanel rosterEmails={rosterEmails} />
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Volunteer Roster</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            onClick={() => loadVolunteers()}>
            Refresh
          </Button>
          {canEdit && (
            <FileButton onChange={handleImport} accept=".csv">
              {(props) => (
                <Button size="xs" variant="light" leftSection={<IconUpload size={14} />} {...props}>
                  Import CSV
                </Button>
              )}
            </FileButton>
          )}
          <Button size="xs" variant="light" leftSection={<IconDownload size={14} />}
            onClick={handleExport} disabled={filtered.length === 0}>
            Export CSV
          </Button>
          {canEdit && (
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
              Add Volunteer
            </Button>
          )}
        </Group>
      </Group>

      {/* Filters */}
      <Group mb="md" gap="sm">
        <TextInput
          placeholder="Search name, email, phone, org, certs..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="xs"
          style={{ flex: 1, minWidth: 200 }}
        />
        <Select data={STATUS_OPTIONS} value={statusFilter}
          onChange={(v) => setStatusFilter(v ?? 'all')} size="xs" w={150} />
        <Select data={ROLE_OPTIONS} value={roleFilter}
          onChange={(v) => setRoleFilter(v ?? 'all')} size="xs" w={150} />
        <Select data={VEHICLE_OPTIONS} value={vehicleFilter}
          onChange={(v) => setVehicleFilter(v ?? 'all')} size="xs" w={140} />
      </Group>

      {/* Pending review banner */}
      {volunteers.filter(v => v.status === 'probationary').length > 0 && statusFilter !== 'probationary' && statusFilter !== 'all' && (
        <Paper p="sm" radius="sm" mb="sm" style={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d' }}>
          <Group gap="sm">
            <Text size="sm" style={{ color: '#92400e' }}>
              ⚠ {volunteers.filter(v => v.status === 'probationary').length} volunteer(s) pending review (probationary status).
            </Text>
            <Button size="xs" variant="subtle" color="orange"
              onClick={() => setStatusFilter('probationary')}>
              Show them
            </Button>
          </Group>
        </Paper>
      )}

      <Text size="xs" c="dimmed" mb="sm">
        Showing {paged.length} of {filtered.length} volunteer(s)
      </Text>

      {/* Table */}
      {paged.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">No volunteers found.</Text>
        </Paper>
      ) : (
        <Paper p={0} radius="sm" withBorder>
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
                <tr style={{ borderBottom: '2px solid var(--frost)' }}>
                  {[
                    { label: 'Name', field: 'lastName' },
                    { label: 'Email', field: 'email' },
                    { label: 'Phone', field: 'phone' },
                    { label: 'Org', field: '' },
                    { label: 'Role', field: '' },
                    { label: 'Status', field: 'status' },
                    { label: 'Certs', field: '' },
                    { label: '', field: '' },
                  ].map(col => (
                    <th
                      key={col.label}
                      onClick={() => col.field && toggleSort(col.field)}
                      style={{
                        textAlign: 'left',
                        padding: '8px 16px',
                        fontWeight: 600,
                        color: sortField === col.field && col.field ? '#4B3FFF' : undefined,
                        whiteSpace: 'nowrap',
                        cursor: col.field ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}>
                      {col.label}
                      {col.field && (
                        <span style={{ marginLeft: 4, opacity: sortField === col.field ? 1 : 0.25 }}>
                          {sortField === col.field && sortDir === 'desc' ? '↓' : '↑'}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.map((v) => (
                  <tr key={v.id} style={{ borderBottom: '1px solid var(--frost)', cursor: 'pointer' }}
                    onClick={() => setDetailVol(v)}>
                    <td style={{ padding: '8px 16px', fontWeight: 600 }}>
                      {v.firstName} {v.lastName}
                    </td>
                    <td style={{ padding: '8px 16px' }}>{v.email || '--'}</td>
                    <td style={{ padding: '8px 16px' }}>{v.phone || '--'}</td>
                    <td style={{ padding: '8px 16px' }}>{(parseExtras(v.notes).org as string) || '--'}</td>
                    <td style={{ padding: '8px 16px', textTransform: 'capitalize' }}>
                      {(parseExtras(v.notes).rolePref as string) || '--'}
                    </td>
                    <td style={{ padding: '8px 16px' }}>
                      {canEdit ? (
                        <Select
                          data={STATUS_OPTIONS.filter((s) => s.value !== 'all')}
                          value={v.status || null}
                          onChange={(val) => { if (val) handleQuickStatus(v, val); }}
                          size="xs"
                          w={120}
                          onClick={(e) => e.stopPropagation()}
                          styles={{ input: { fontWeight: 600, fontSize: 12 } }}
                        />
                      ) : (
                        <Badge size="xs" color={v.status === 'active' ? 'var(--color-success)' : v.status === 'dnd' ? 'var(--color-error)' : '#939598'}>
                          {v.status}
                        </Badge>
                      )}
                    </td>
                    <td style={{ padding: '8px 16px' }}>
                      <Group gap={4} wrap="wrap">
                        {v.certifications
                          ? v.certifications.split(',').filter(Boolean).map((c) => (
                              <Badge key={c.trim()} size="xs" variant="light">
                                {c.trim()}
                              </Badge>
                            ))
                          : <Text size="xs" c="dimmed">--</Text>}
                      </Group>
                    </td>
                    <td style={{ padding: '8px 16px' }} onClick={(e) => e.stopPropagation()}>
                      {canEdit && (
                        <Group gap={4}>
                          <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(v)}>
                            <IconPencil size={14} />
                          </ActionIcon>
                          <ActionIcon size="sm" variant="subtle" color="var(--color-error)"
                            onClick={() => setDeleteTarget(v)}>
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Group>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Paper>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
        </Group>
      )}

      {/* Create Modal */}
      <Modal opened={showCreate} onClose={() => { setShowCreate(false); resetForm(); }}
        title="Add Volunteer" size="lg" centered>
        {formBody}
      </Modal>

      {/* Edit Modal */}
      <Modal opened={!!editVol} onClose={() => { setEditVol(null); resetForm(); }}
        title="Edit Volunteer" size="lg" centered>
        {formBody}
      </Modal>

      {/* Detail Modal */}
      <Modal opened={!!detailVol} onClose={() => setDetailVol(null)}
        title={detailVol ? `${detailVol.firstName} ${detailVol.lastName}` : ''}
        size="lg" centered>
        {detailVol && (
          <Tabs defaultValue="contact">
            <Tabs.List mb="md">
              <Tabs.Tab value="contact" leftSection={<IconUser size={14} />}>Contact</Tabs.Tab>
              <Tabs.Tab value="emergency" leftSection={<IconPhone size={14} />}>Emergency</Tabs.Tab>
              <Tabs.Tab value="skills" leftSection={<IconCertificate size={14} />}>Skills</Tabs.Tab>
              <Tabs.Tab value="credentials" leftSection={<IconId size={14} />}>Credentials</Tabs.Tab>
              <Tabs.Tab value="notes" leftSection={<IconNotes size={14} />}>Notes</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="contact">
              <Stack gap="xs">
                <Group gap="lg">
                  <div>
                    <Text size="xs" c="dimmed">Name</Text>
                    <Text size="sm" fw={600}>{detailVol.firstName} {detailVol.lastName}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Organization</Text>
                    <Text size="sm">{(detailExtras.org as string) || '--'}</Text>
                  </div>
                </Group>
                <Group gap="lg">
                  <div>
                    <Text size="xs" c="dimmed">Email</Text>
                    <Text size="sm">{detailVol.email || '--'}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Phone</Text>
                    <Text size="sm">{detailVol.phone ? formatPhone(detailVol.phone) : '--'}</Text>
                  </div>
                </Group>
                <div>
                  <Text size="xs" c="dimmed">Role Preference</Text>
                  <Badge size="sm" variant="light" style={{ textTransform: 'capitalize' }}>
                    {(detailExtras.rolePref as string) || 'active'}
                  </Badge>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Status</Text>
                  <Badge size="sm" color={statusColor(detailVol.status)}>
                    {detailVol.status}
                  </Badge>
                </div>
                {!!(detailExtras.dob || detailExtras.address || detailExtras.shirtSize || detailExtras.dietary) && (
                  <>
                    {detailExtras.dob && (
                      <div>
                        <Text size="xs" c="dimmed">Date of Birth</Text>
                        <Text size="sm">{String(detailExtras.dob)}</Text>
                      </div>
                    )}
                    {(detailExtras.address || detailExtras.city) && (
                      <div>
                        <Text size="xs" c="dimmed">Address</Text>
                        <Text size="sm">
                          {[detailExtras.address, detailExtras.city, detailExtras.state, detailExtras.zip]
                            .filter(Boolean).map(String).join(', ')}
                        </Text>
                      </div>
                    )}
                    {detailExtras.shirtSize && (
                      <div>
                        <Text size="xs" c="dimmed">T-Shirt Size</Text>
                        <Text size="sm">{String(detailExtras.shirtSize)}</Text>
                      </div>
                    )}
                    {detailExtras.dietary && (
                      <div>
                        <Text size="xs" c="dimmed">Dietary Restrictions</Text>
                        <Text size="sm">{String(detailExtras.dietary)}</Text>
                      </div>
                    )}
                  </>
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="emergency">
              <Stack gap="xs">
                <div>
                  <Text size="xs" c="dimmed">Emergency Contact Name</Text>
                  <Text size="sm" fw={600}>{detailVol.emergencyContact || '--'}</Text>
                </div>
                {!!detailExtras.ecRelationship && (
                  <div>
                    <Text size="xs" c="dimmed">Relationship</Text>
                    <Text size="sm">{String(detailExtras.ecRelationship)}</Text>
                  </div>
                )}
                <div>
                  <Text size="xs" c="dimmed">Emergency Contact Phone</Text>
                  <Text size="sm">{detailVol.emergencyPhone ? formatPhone(detailVol.emergencyPhone) : '--'}</Text>
                </div>
                {!!detailExtras.ecEmail && (
                  <div>
                    <Text size="xs" c="dimmed">Emergency Contact Email</Text>
                    <Text size="sm">{String(detailExtras.ecEmail)}</Text>
                  </div>
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="skills">
              <Stack gap="sm">
                <div>
                  <Text size="xs" c="dimmed" mb={4}>Certifications</Text>
                  <Group gap={4} wrap="wrap">
                    {detailVol.certifications
                      ? detailVol.certifications.split(',').filter(Boolean).map((c) => (
                          <Badge key={c.trim()} size="sm" variant="light" color="var(--color-primary-700)">
                            {c.trim()}
                          </Badge>
                        ))
                      : <Text size="sm" c="dimmed">None</Text>}
                  </Group>
                </div>
                <div>
                  <Text size="xs" c="dimmed" mb={4}>Skills</Text>
                  <Group gap={4} wrap="wrap">
                    {detailVol.skills
                      ? detailVol.skills.split(',').filter(Boolean).map((s) => (
                          <Badge key={s.trim()} size="sm" variant="light" color="var(--data-viz-650)">
                            {s.trim()}
                          </Badge>
                        ))
                      : <Text size="sm" c="dimmed">None</Text>}
                  </Group>
                </div>
                {!!detailExtras.equipment && (
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>Equipment</Text>
                    <Group gap={4} wrap="wrap">
                      {String(detailExtras.equipment).split(',').filter(Boolean).map((eq) => (
                        <Badge key={eq.trim()} size="sm" variant="light" color="#F86A0B">
                          {eq.trim()}
                        </Badge>
                      ))}
                    </Group>
                  </div>
                )}
                {!!detailExtras.languages && (
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>Languages</Text>
                    <Text size="sm">{String(detailExtras.languages)}</Text>
                  </div>
                )}
                {!!detailExtras.vehicle && (
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>Vehicle</Text>
                    <Badge size="sm" variant="light" color="#03AC9E" style={{ textTransform: 'capitalize' }}>
                      {String(detailExtras.vehicle)}
                    </Badge>
                  </div>
                )}
                <div>
                  <Text size="xs" c="dimmed" mb={4}>Availability</Text>
                  {detailVol.availability && detailVol.availability !== '{}'
                    ? (() => {
                        try {
                          const avail = JSON.parse(detailVol.availability) as Record<string, Record<string, boolean>>;
                          const days = Object.keys(avail);
                          if (days.length === 0) return <Text size="sm" c="dimmed">Not set</Text>;
                          return (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, fontSize: 12 }}>
                              {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((day) => {
                                const slots = avail[day] || {};
                                const active = Object.entries(slots).filter(([, v]) => v).map(([k]) => k);
                                return (
                                  <Paper key={day} p={4} radius="sm" withBorder ta="center">
                                    <Text size="xs" fw={700} tt="uppercase">{day}</Text>
                                    {active.length > 0
                                      ? active.map((slot) => (
                                          <Badge key={slot} size="xs" variant="dot" color="var(--color-success)" mt={2}>
                                            {slot}
                                          </Badge>
                                        ))
                                      : <Text size="xs" c="dimmed">--</Text>}
                                  </Paper>
                                );
                              })}
                            </div>
                          );
                        } catch {
                          return <Text size="sm" c="dimmed">Invalid data</Text>;
                        }
                      })()
                    : <Text size="sm" c="dimmed">Not set</Text>}
                </div>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="credentials">
              <Stack gap="sm">
                {TABLES.credentials && FIELDS.credentials.personId && FIELDS.volunteers.personId ? (
                  (() => {
                    const personIdField = FIELDS.volunteers.personId;
                    const linkedPersonId = (detailVol as any)[personIdField];
                    if (!linkedPersonId) {
                      return (
                        <div style={{ padding: '16px 16px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 4 }}>
                          <Text size="sm" c="dimmed">This volunteer is not linked to a directory person record. Link them from the People Directory to view credentials.</Text>
                        </div>
                      );
                    }
                    if (detailCredsLoading) {
                      return <Text size="sm" c="dimmed">Loading credentials…</Text>;
                    }
                    if (detailCreds.length === 0) {
                      return <Text size="sm" c="dimmed">No credentials on file in the directory.</Text>;
                    }
                    return (
                      <div style={{ display: 'grid', gap: 8 }}>
                        {detailCreds.map(c => {
                          const isExpired = c.expiresDate && new Date(c.expiresDate) < new Date();
                          const expiringSoon = c.expiresDate && !isExpired && new Date(c.expiresDate) < new Date(Date.now() + 90 * 86400000);
                          return (
                            <Paper key={c.record_id} p="sm" withBorder radius="sm">
                              <Group justify="space-between" mb={2}>
                                <Text size="sm" fw={600}>{c.name || '—'}</Text>
                                <Badge size="xs" color={isExpired ? 'var(--color-error)' : expiringSoon ? '#C68700' : 'var(--color-success)'} variant="light">
                                  {isExpired ? 'Expired' : expiringSoon ? 'Expiring Soon' : c.status || 'Active'}
                                </Badge>
                              </Group>
                              <Group gap={12}>
                                {c.type && <Text size="xs" c="dimmed">{c.type}</Text>}
                                {c.issuingBody && <Text size="xs" c="dimmed">{c.issuingBody}</Text>}
                                {c.certCode && <Text size="xs" c="dimmed">#{c.certCode}</Text>}
                              </Group>
                              {(c.issuedDate || c.expiresDate) && (
                                <Group gap={12} mt={2}>
                                  {c.issuedDate && <Text size="xs" c="dimmed">Issued: {new Date(c.issuedDate).toLocaleDateString()}</Text>}
                                  {c.expiresDate && <Text size="xs" c={isExpired ? 'var(--color-error)' : expiringSoon ? 'yellow.7' : 'dimmed'}>Expires: {new Date(c.expiresDate).toLocaleDateString()}</Text>}
                                </Group>
                              )}
                            </Paper>
                          );
                        })}
                      </div>
                    );
                  })()
                ) : (
                  <div>
                    <Text size="xs" c="dimmed" mb={8}>Certifications (from volunteer record)</Text>
                    <Group gap={4} wrap="wrap">
                      {detailVol?.certifications
                        ? detailVol.certifications.split(',').filter(Boolean).map((c) => (
                            <Badge key={c.trim()} size="sm" variant="light" color="var(--color-primary-700)">{c.trim()}</Badge>
                          ))
                        : <Text size="sm" c="dimmed">None on file</Text>}
                    </Group>
                    <Text size="xs" c="dimmed" mt={12} style={{ fontStyle: 'italic' }}>
                      Live credential records from the directory will appear here once the Credentials table is provisioned.
                    </Text>
                  </div>
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="notes">
              <Stack gap="xs">
                {!!detailExtras.waiverAccepted && (
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>Waiver Status</Text>
                    <Group gap="xs">
                      <Badge size="sm" color="var(--color-success)" variant="light">Waiver Signed</Badge>
                      {!!detailExtras.waiverDate && (
                        <Text size="xs" c="dimmed">
                          on {new Date(String(detailExtras.waiverDate)).toLocaleDateString()}
                        </Text>
                      )}
                    </Group>
                  </div>
                )}
                {detailVol.notes && (
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>Notes / Extra Fields</Text>
                    {Object.keys(detailExtras).length > 0
                      ? (
                          <Paper p="sm" radius="sm" withBorder>
                            {Object.entries(detailExtras).map(([key, val]) => (
                              <Group key={key} gap="xs" mb={2}>
                                <Text size="xs" fw={600} style={{ textTransform: 'capitalize' }}>
                                  {key}:
                                </Text>
                                <Text size="xs">{String(val)}</Text>
                              </Group>
                            ))}
                          </Paper>
                        )
                      : <Text size="sm">{detailVol.notes}</Text>}
                  </div>
                )}
                {!detailVol.notes && !detailExtras.waiverAccepted && (
                  <Text size="sm" c="dimmed">No notes.</Text>
                )}
              </Stack>
            </Tabs.Panel>
          </Tabs>
        )}
        {detailVol && canEdit && (
          <Group justify="flex-end" mt="md">
            <Button size="xs" variant="light" leftSection={<IconPencil size={14} />}
              onClick={() => { setDetailVol(null); openEdit(detailVol); }}>
              Edit
            </Button>
          </Group>
        )}
      </Modal>

      {/* Delete Confirm */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        title="Remove Volunteer" centered size="sm">
        <Text size="sm" mb="md">
          Remove {deleteTarget?.firstName} {deleteTarget?.lastName} from the roster?
          This action cannot be undone.
        </Text>
        <Group justify="flex-end">
          <Button variant="light" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="var(--color-error)" onClick={handleDelete} loading={saving}>Remove</Button>
        </Group>
      </Modal>
    </div>
  );
}
